import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { configureApiClient } from "@porchpass/api-client";
import { auth } from "src/api/firebase";
import { initializeGA } from "src/api/external/google-analytics";
import * as Sentry from "@sentry/react";
import { useAuth, useUser } from "src/context";
import { useInactivityTimeout } from "src/hooks";
import { getDealerNameFromUrl, dealerStorage } from "src/utils";

/**
 * AppInitializer is responsible for running app-wide hooks or startup logic
 * as singletons at the top level of the application. It renders children
 * as a passthrough and ensures any side effects only occur once.
 */
export const AppInitializer = ({ children }: { children: React.ReactNode }) => {
  const { userData, setUserData } = useUser();
  const { setUser, logout } = useAuth();
  const navigate = useNavigate();
  const dealerName = getDealerNameFromUrl();
  const { pathname: currentPath } = useLocation();

  const { startInactivityTimer, stopInactivityTimer } =
    useInactivityTimeout(handleLogout);

  useEffect(() => {
    initializeGA();
    configureApiClient({
      baseUrl: __API_BASE_URL__,
      getAuthToken: async () => {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          return "";
        }
        return currentUser.getIdToken();
      },
    });
  }, []);

  useEffect(() => {
    const unsubcribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      updateSentryUser(currentUser);
      if (currentUser) {
        startInactivityTimer();
      } else {
        stopInactivityTimer();
        setUserData?.((prev) => ({
          dealer_id: prev.dealer_id,
        }));
      }
    });

    return () => {
      unsubcribe();
    };
  }, []);

  useEffect(() => {
    if (
      setUserData &&
      userData &&
      userData.dealer_id &&
      userData.dealer_id !== dealerName
    ) {
      console.warn(
        "Dealer context doesn't match URL. Resetting dealer_id and routing to dealer home.",
      );
      setUserData({
        dealer_id: dealerName,
      });
      return navigate(`/${dealerName}`);
    }
  }, [userData]);

  /**
   * Add Firebase user id to Sentry user context when logging in.
   * Remove uid from Sentry context when logging out.
   */
  const updateSentryUser = (user: typeof auth.currentUser | null) => {
    if (user) {
      Sentry.setUser({ id: user.uid }); // Set Sentry user context with auth user ID
    } else {
      Sentry.setUser(null); // Clear Sentry user context on logout or null user
    }
  };

  async function handleLogout() {
    const params = new URLSearchParams();
    params.set("redirect", encodeURIComponent(currentPath));
    params.set("error", "session-inactivity");
    const logoutUrl = `/${dealerName}/login?${params.toString()}`;

    await logout();
    dealerStorage.clear();
    navigate(logoutUrl);
  }

  return <>{children}</>;
};
